import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { Circle, MessageSquare, ChevronLeft, ChevronRight, TrendingUp, TrendingDown, BarChart3, Trash2, StickyNote, Activity } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  ResponsiveContainer, ComposedChart, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine,
} from "recharts";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "../contexts/AuthContext";
import { Toaster, toast } from "@/components/ui/sonner";
import PageHeader from "@/components/PageHeader";
import DonutChart from "@/components/DonutChart";
import CsvExportPopover from "@/components/CsvExportPopover";
import UnmatchedFeedbackPanel from "@/components/UnmatchedFeedbackPanel";
import { cachedGet } from "@/lib/apiCache";

const API = window.location.origin + "/api";

function DotRating({ rating }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((s) => (
        <Circle
          key={s}
          className={`w-3.5 h-3.5 ${s <= rating ? "text-blue-500 fill-blue-500" : "text-slate-200"}`}
        />
      ))}
    </div>
  );
}

function StatCard({ label, value, sub, icon: Icon, color, integer = false }) {
  let display = value;
  if (typeof value === "number") {
    // Truncate to 2 decimals (no rounding) so e.g. 4.789 → 4.78, not 4.79
    display = integer
      ? value.toLocaleString("it-IT")
      : (Math.trunc(value * 100) / 100).toFixed(2);
  }
  return (
    <div className="bg-white rounded-xl border border-slate-100 p-4 flex items-center gap-3" data-testid={`stat-${label}`}>
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${color}`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div>
        <div className="text-2xl font-bold text-slate-800">{display}</div>
        <div className="text-xs text-slate-400">{label}</div>
        {sub && <div className="text-[10px] text-slate-300 mt-0.5">{sub}</div>}
      </div>
    </div>
  );
}

function RatingBar({ label, avg, color }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1">
      <div className="flex items-center gap-2 min-w-0 flex-1">
        {color && <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />}
        <span className="text-sm text-slate-600 truncate">{label}</span>
      </div>
      <span className="text-sm font-bold text-slate-800 tabular-nums shrink-0">
        {(Math.trunc(Number(avg || 0) * 100) / 100).toFixed(2)}
      </span>
    </div>
  );
}

const MONTH_NAMES = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];

// Shared colour palette used by both MonthlyChart lines and RatingBar dots.
const QUESTION_COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4", "#ec4899", "#84cc16"];

function MonthlyChart({ monthly, questions }) {
  // One line per question (no "Media" overall line)
  const lines = questions.map((q, i) => ({
    key: q.id,
    label: q.text,
    color: QUESTION_COLORS[i % QUESTION_COLORS.length],
  }));

  // Build the window: mostra TUTTI i mesi con dato (richiesta utente
  // 31/07/2026) più i 2 mesi futuri, così l'operatore vede l'andamento
  // completo dai primi feedback registrati fino al periodo in
  // programmazione. Se non ci sono dati storici, fallback ai 5 mesi
  // classici (da -2 a +2 rispetto ad oggi).
  const monthlyByKey = {};
  (monthly || []).forEach((m) => { monthlyByKey[m.month] = m; });

  const today = new Date();
  const months = [];
  const dataKeys = Object.keys(monthlyByKey).sort();
  // Primo mese: min(dato più vecchio, oggi-2). Ultimo mese: oggi+2.
  const oldestData = dataKeys.length ? dataKeys[0] : null;
  const earliestOffset = (() => {
    if (!oldestData) return -2;
    const [y, m] = oldestData.split("-").map(Number);
    // offset (rispetto al mese corrente) in mesi
    const off = (y - today.getFullYear()) * 12 + (m - 1 - today.getMonth());
    return Math.min(off, -2);
  })();
  for (let off = earliestOffset; off <= 2; off++) {
    const d = new Date(today.getFullYear(), today.getMonth() + off, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const real = monthlyByKey[key];
    months.push(real || { month: key, count: 0 });
  }

  // Fixed Y-axis scale 2.50 → 5.00 (richiesta utente 02/06/2026).
  // Scala fissa così l'utente può confrontare a colpo d'occhio mesi
  // diversi (la precedente scala dinamica zoom-ava sui dati reali e
  // rendeva il confronto poco intuitivo). Step 0.5 → 6 tick (2.5, 3, 3.5, 4, 4.5, 5).
  const minVal = 2.5;
  const maxVal = 5;
  const range = maxVal - minVal;

  // Use viewBox + 100% width → SVG scales to container, fixed proportional spacing
  const chartH = 220;
  const chartW = 480;
  const padL = 40;
  const padR = 16;
  const padT = 12;
  const padB = 32;
  const plotW = chartW - padL - padR;
  const plotH = chartH - padT - padB;

  const tickStep = 0.5;
  const ticks = [];
  for (let v = minVal; v <= maxVal + 1e-9; v += tickStep) {
    ticks.push(Math.round(v * 100) / 100);
  }

  const xStep = months.length > 1 ? plotW / (months.length - 1) : plotW / 2;
  const getY = (val) => padT + plotH - ((val - minVal) / range) * plotH;
  const getX = (i) => padL + (months.length > 1 ? i * xStep : plotW / 2);

  const formatMonth = (m) => {
    const parts = m.split("-");
    if (parts.length === 2) return MONTH_NAMES[parseInt(parts[1]) - 1] || m;
    return m;
  };

  return (
    <div className="bg-slate-50 rounded-xl border border-slate-100 p-4 flex flex-col" data-testid="monthly-chart">
      <h3 className="text-sm font-semibold text-slate-700 mb-3">Andamento Mensile</h3>

      <svg viewBox={`0 0 ${chartW} ${chartH}`} preserveAspectRatio="xMidYMid meet" className="w-full flex-1 min-h-0">
        {ticks.map((v) => (
          <g key={v}>
            <line x1={padL} y1={getY(v)} x2={chartW - padR} y2={getY(v)} stroke="#e2e8f0" strokeDasharray="3,3" />
            <text x={padL - 4} y={getY(v) + 4} textAnchor="end" className="fill-slate-500 tabular-nums" style={{ fontSize: 12, fontWeight: 500 }}>
              {v.toFixed(1)}
            </text>
          </g>
        ))}

        {lines.map((l) => {
          const visiblePoints = months
            .map((m, i) => ({ i, val: m[l.key] }))
            // Filtra valori 0/null E fuori scala (>5 o <2.5). Il filtro
            // sulla scala fissa 2.5-5 evita di disegnare punti sotto/sopra
            // l'asse Y visibile (es. recensione 1.8 stelle resterebbe
            // invisibile col precedente filter `> 0` ma il valore sotto
            // minVal andrebbe in negativo nel calcolo getY).
            .filter((p) => typeof p.val === "number" && p.val >= minVal && p.val <= maxVal);
          if (visiblePoints.length === 0) return null;
          const polyPoints = visiblePoints.map((p) => `${getX(p.i)},${getY(p.val)}`).join(" ");
          return (
            <g key={l.key}>
              <polyline
                points={polyPoints}
                fill="none"
                stroke={l.color}
                strokeWidth={1.6}
                strokeLinejoin="round"
                strokeLinecap="round"
              />
              {visiblePoints.map((p) => (
                <circle
                  key={`${l.key}-${p.i}`}
                  cx={getX(p.i)}
                  cy={getY(p.val)}
                  r={2.8}
                  fill={l.color}
                  stroke="white"
                  strokeWidth={1.2}
                >
                  <title>{`${l.label}: ${(Math.trunc(p.val * 100) / 100).toFixed(2)}`}</title>
                </circle>
              ))}
            </g>
          );
        })}

        {months.map((m, i) => (
          <text key={m.month} x={getX(i)} y={chartH - 6} textAnchor="middle" className="fill-slate-700" style={{ fontSize: 13, fontWeight: 600 }}>
            {formatMonth(m.month)}
          </text>
        ))}
      </svg>
    </div>
  );
}

export default function FeedbackPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [feedbacks, setFeedbacks] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [stats, setStats] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const currentMonthKey = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  }, []);
  const [monthFilter, setMonthFilter] = useState(currentMonthKey);
  const [dailyRate, setDailyRate] = useState(null);

  const fetchDailyRate = useCallback(async (m) => {
    if (!m || m === "all") { setDailyRate(null); return; }
    try {
      const { data } = await axios.get(
        `${API}/feedback/daily-vs-appointments?month=${encodeURIComponent(m)}`,
        { withCredentials: true }
      );
      setDailyRate(data);
    } catch (err) {
      console.error("fetchDailyRate:", err);
      setDailyRate(null);
    }
  }, []);

  const fetchQuestions = useCallback(async () => {
    try {
      const { data } = await cachedGet(`${API}/feedback/questions`, { ttl: 300000 });
      setQuestions(data.questions || []);
    } catch (err) {
      console.error("fetchQuestions:", err);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/feedback/stats`, { withCredentials: true });
      setStats(data);
    } catch (err) {
      console.error("fetchStats:", err);
    }
  }, []);

  const fetchFeedbacks = useCallback(async (p, month = "all") => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/feedback/list?page=${p}&limit=30&month=${encodeURIComponent(month)}`,
        { withCredentials: true }
      );
      setFeedbacks(data.feedbacks || []);
      setTotalPages(data.pages || 1);
      setTotal(data.total || 0);
    } catch (err) {
      console.error("fetchFeedbacks:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchQuestions();
    fetchStats();
  }, [fetchQuestions, fetchStats]);

  // Re-fetch when filter or page changes
  useEffect(() => {
    fetchFeedbacks(page, monthFilter);
  }, [page, monthFilter, fetchFeedbacks]);

  // Daily appointments-vs-feedback chart updates with the selected month
  useEffect(() => {
    fetchDailyRate(monthFilter === "all" ? currentMonthKey : monthFilter);
  }, [monthFilter, currentMonthKey, fetchDailyRate]);

  // Reset page to 1 when month filter changes
  useEffect(() => {
    setPage(1);
  }, [monthFilter]);

  const handlePage = (p) => {
    setPage(p);
  };

  const handleDelete = async (feedbackId) => {
    try {
      await axios.delete(`${API}/feedback/delete/${feedbackId}`, { withCredentials: true });
      fetchFeedbacks(page, monthFilter);
      fetchStats();
    } catch (err) {
      console.error("delete feedback:", err);
    }
  };

  const _handleDeleteAll = async () => {
    if (!window.confirm("Vuoi cancellare TUTTI i feedback? Questa azione è irreversibile.")) return;
    try {
      await axios.delete(`${API}/feedback/delete-all`, { withCredentials: true });
      setMonthFilter("all");
      setPage(1);
      fetchFeedbacks(1, "all");
      fetchStats();
    } catch (err) {
      console.error("delete all:", err);
    }
  };

  // Internal note dialog state
  const [noteDialog, setNoteDialog] = useState({ open: false, fbId: null, value: "" });
  const [savingNote, setSavingNote] = useState(false);

  const openNote = (fb) => setNoteDialog({ open: true, fbId: fb.feedback_id, value: fb.internal_note || "" });
  const closeNote = () => setNoteDialog({ open: false, fbId: null, value: "" });

  const saveNote = async () => {
    if (!noteDialog.fbId) return;
    setSavingNote(true);
    try {
      await axios.put(
        `${API}/feedback/${noteDialog.fbId}/note`,
        { note: noteDialog.value },
        { withCredentials: true }
      );
      // Patch the feedback in local state so we don't refetch the whole page
      setFeedbacks((prev) => prev.map((fb) =>
        fb.feedback_id === noteDialog.fbId
          ? { ...fb, internal_note: noteDialog.value }
          : fb
      ));
      toast.success("Nota salvata");
      closeNote();
    } catch (err) {
      console.error("save note:", err);
      toast.error("Errore durante il salvataggio della nota");
    } finally {
      setSavingNote(false);
    }
  };

  // Group feedbacks by year+month for the visible page
  const groupedByMonth = useMemo(() => {
    const months = new Map();
    for (const fb of feedbacks) {
      const d = new Date(fb.submitted_at);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const label = d.toLocaleDateString("it-IT", { month: "long", year: "numeric" });
      if (!months.has(key)) months.set(key, { key, label, items: [] });
      months.get(key).items.push(fb);
    }
    return Array.from(months.values());
  }, [feedbacks]);

  // Build the list of available months from the stats payload (covers ALL
  // history, not just the current page) so the user can jump straight to
  // any month with feedbacks. The current month is always included even
  // if it has no feedback yet, so the dropdown can default to it.
  const availableMonths = useMemo(() => {
    const monthly = stats?.monthly || [];
    const keys = new Set();
    const opts = [];
    const pushKey = (key) => {
      if (!key || keys.has(key)) return;
      keys.add(key);
      const [y, mm] = key.split("-");
      const date = new Date(Number(y), Number(mm) - 1, 1);
      opts.push({
        value: key,
        label: date.toLocaleDateString("it-IT", { month: "long", year: "numeric" }),
      });
    };
    pushKey(currentMonthKey);
    for (const m of monthly) pushKey(m?.month);
    return opts.sort((a, b) => b.value.localeCompare(a.value));
  }, [stats, currentMonthKey]);

  const qMap = {};
  questions.forEach((q) => { qMap[q.id] = q.text; });

  const getTypeLabel = (type) => {
    if (type === "principale") return "Principale";
    if (type === "secondario") return "Secondario";
    return null;
  };

  const getTypeBadgeClass = (type) => {
    if (type === "principale") return "bg-blue-100 text-blue-700";
    if (type === "secondario") return "bg-slate-100 text-slate-600";
    return "";
  };

  const pq = stats?.per_question || {};

  // Compute previous month key (e.g. current "2026-05" → "2026-04")
  const previousMonthKey = useMemo(() => {
    const [y, m] = currentMonthKey.split("-").map(Number);
    const d = new Date(y, m - 2, 1); // m-1 is current month idx, m-2 is previous
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  }, [currentMonthKey]);

  const monthLabel = (key) => {
    const [y, mm] = key.split("-").map(Number);
    return new Date(y, mm - 1, 1).toLocaleDateString("it-IT", { month: "long", year: "numeric" });
  };

  // Build per-question avg for a given month using stats.monthly array
  const monthlyByKey = useMemo(() => {
    const map = {};
    (stats?.monthly || []).forEach((m) => { map[m.month] = m; });
    return map;
  }, [stats]);

  const currentMonthData = monthlyByKey[currentMonthKey];
  const previousMonthData = monthlyByKey[previousMonthKey];

  // Esporta CSV con selettore di periodo (Settimana/Mese/Tutti). Usa
  // l'endpoint backend `/feedback/export` che gestisce già il calcolo
  // del range in base a period + offset.
  const handleExportCSV = async (period, offset, label) => {
    try {
      const params = { period };
      if (period === "week" || period === "month") params.offset = offset;
      const res = await axios.get(`${API}/feedback/export`, {
        params,
        withCredentials: true,
        responseType: "blob",
      });
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const filename = m ? m[1] : `feedback_${period}.csv`;
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { a.remove(); window.URL.revokeObjectURL(url); }, 1000);
      toast.success(`CSV scaricato: ${label}`);
    } catch (e) {
      console.error(e);
      toast.error("Errore durante il download");
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 bg-grid-light pt-[calc(7px+3.5rem+13px)] pb-10 px-4 lg:px-[100px]">
      <Toaster position="top-right" theme="light" />
      <div className="w-full">
        {/* Header */}
        <PageHeader
          title="Feedback"
          subtitle="Risposte di gradimento raccolte dai pazienti"
          actions={
            <CsvExportPopover
              title="Esporta feedback in CSV"
              testIdPrefix="feedback-export"
              onDownload={handleExportCSV}
            />
          }
        />
      </div>

      {/* Single white form card containing dashboard + feedback list */}
      <div className="w-full bg-white rounded-3xl shadow-md p-4 sm:p-8 -mt-8 relative z-10 space-y-6">
        {/* Sync feedback → GHL contact notes */}
        <UnmatchedFeedbackPanel isAdmin={isAdmin} />

        {/* Dashboard */}
        {stats && stats.total > 0 && (
          <div className="space-y-4">
            {/* Stat cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <StatCard label="Feedback ricevuti" value={stats.total} icon={MessageSquare} color="bg-blue-500" integer />
              <StatCard label="Voto medio generale" value={stats.overall_avg} sub="su 5" icon={BarChart3} color="bg-indigo-500" />
              {stats.best && (
                <StatCard label="Miglior categoria" value={stats.best.avg} sub={stats.best.text} icon={TrendingUp} color="bg-emerald-500" />
              )}
              {stats.worst && (
                <StatCard label="Da migliorare" value={stats.worst.avg} sub={stats.worst.text} icon={TrendingDown} color="bg-amber-500" />
              )}
            </div>

            {/* Per-question bars + monthly chart — aligned to the 4-col stat cards above */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-slate-50 rounded-xl border border-slate-100 p-4" data-testid="per-question-bars-previous">
                <h3 className="text-sm font-semibold text-slate-700 mb-4 capitalize">
                  Voti Medi per {monthLabel(previousMonthKey)}
                </h3>
                {previousMonthData ? (
                  <div className="space-y-3">
                    {questions.map((q, i) => (
                      <RatingBar
                        key={q.id}
                        label={pq[q.id]?.text || q.text}
                        avg={(previousMonthData && previousMonthData[q.id]) || 0}
                        color={QUESTION_COLORS[i % QUESTION_COLORS.length]}
                      />
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-slate-400 italic">Nessun feedback registrato nel mese precedente.</p>
                )}
              </div>
              <div className="bg-slate-50 rounded-xl border border-slate-100 p-4" data-testid="per-question-bars-current">
                <h3 className="text-sm font-semibold text-slate-700 mb-4 capitalize">
                  Voti Medi per {monthLabel(currentMonthKey)}
                </h3>
                <div className="space-y-3">
                  {questions.map((q, i) => (
                    <RatingBar
                      key={q.id}
                      label={pq[q.id]?.text || q.text}
                      avg={(currentMonthData && currentMonthData[q.id]) || 0}
                      color={QUESTION_COLORS[i % QUESTION_COLORS.length]}
                    />
                  ))}
                </div>
              </div>
              <div className="lg:col-span-2 md:col-span-2">
                <MonthlyChart monthly={stats.monthly} questions={questions} />
              </div>
            </div>

            {/* Demographics donuts */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <DonutChart
                title="Pazienti Unici / Secondari"
                align="left"
                data={[
                  { label: "Unico", value: stats.by_patient_type?.principale || 0, color: "#3b82f6" },
                  { label: "Secondario", value: stats.by_patient_type?.secondario || 0, color: "#10b981" },
                  { label: "Non specificato", value: stats.by_patient_type?.non_specificato || 0, color: "#cbd5e1" },
                ]}
              />
              <DonutChart
                title="Uomini / Donne"
                data={[
                  { label: "Uomini", value: stats.by_gender?.M || 0, color: "#0ea5e9" },
                  { label: "Donne", value: stats.by_gender?.F || 0, color: "#ec4899" },
                  { label: "Non specificato", value: stats.by_gender?.non_specificato || 0, color: "#cbd5e1" },
                ]}
              />
              <DonutChart
                title="Fasce di età"
                align="left"
                data={[
                  { label: "0-18", value: stats.by_age_group?.["0-18"] || 0, color: "#a78bfa" },
                  { label: "19-30", value: stats.by_age_group?.["19-30"] || 0, color: "#60a5fa" },
                  { label: "31-50", value: stats.by_age_group?.["31-50"] || 0, color: "#34d399" },
                  { label: "51-70", value: stats.by_age_group?.["51-70"] || 0, color: "#fbbf24" },
                  { label: "71+", value: stats.by_age_group?.["71+"] || 0, color: "#f87171" },
                  { label: "Non specificato", value: stats.by_age_group?.non_specificato || 0, color: "#cbd5e1" },
                ]}
              />
            </div>

            {/* Daily appointments vs feedbacks chart */}
            {dailyRate && (
              <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5" data-testid="feedback-daily-chart">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <h2 className="text-sm font-bold text-slate-700 flex items-center gap-2">
                    <Activity className="w-4 h-4 text-indigo-500" /> Rapporto Appuntamenti / Feedback — {dailyRate.month}
                  </h2>
                  <div className="flex items-center gap-3 text-xs text-slate-500">
                    <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-indigo-200" /> {dailyRate.totals.appointments} app.</span>
                    <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-amber-500" /> {dailyRate.totals.feedbacks} feedback</span>
                    <span className="inline-flex items-center gap-1.5" title={dailyRate.totals.rate_from && dailyRate.totals.rate_to ? `Tasso calcolato dal ${dailyRate.totals.rate_from} al ${dailyRate.totals.rate_to}` : ""}>
                      <span className="w-3 h-0.5 bg-emerald-500" />
                      tasso medio <strong className="text-emerald-700">{dailyRate.totals.rate}%</strong>
                      {dailyRate.totals.appointments_until_today !== undefined
                        && dailyRate.totals.appointments_until_today !== dailyRate.totals.appointments && (
                          <span className="text-slate-400">({dailyRate.totals.feedbacks_until_today}/{dailyRate.totals.appointments_until_today})</span>
                      )}
                    </span>
                  </div>
                </div>
                <div style={{ width: "100%", height: 280 }}>
                  <ResponsiveContainer>
                    <ComposedChart data={dailyRate.series} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
                      <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" />
                      <XAxis dataKey="date" tickFormatter={(v) => v.slice(8, 10) + "/" + v.slice(5, 7)} tick={{ fontSize: 11, fill: "#94a3b8" }} />
                      <YAxis yAxisId="left" tick={{ fontSize: 11, fill: "#94a3b8" }} allowDecimals={false} />
                      <YAxis yAxisId="right" orientation="right" domain={[0, 20]} tick={{ fontSize: 11, fill: "#10b981" }} unit="%" />
                      <Tooltip
                        labelFormatter={(v) => `Giorno ${v.slice(8, 10)}/${v.slice(5, 7)}`}
                        contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }}
                        formatter={(value, name) => {
                          if (name === "rate") return [`${value}%`, "Tasso feedback"];
                          if (name === "appointments") return [value, "Appuntamenti"];
                          if (name === "feedbacks") return [value, "Feedback"];
                          return [value, name];
                        }}
                      />
                      <Legend wrapperStyle={{ fontSize: 11 }} formatter={(v) => v === "rate" ? "Tasso feedback (%)" : v === "appointments" ? "Appuntamenti" : "Feedback"} />
                      <Bar yAxisId="left" dataKey="appointments" name="appointments" fill="#c7d2fe" />
                      <Bar yAxisId="left" dataKey="feedbacks" name="feedbacks" fill="#f59e0b" />
                      <Line yAxisId="right" type="monotone" dataKey="rate" name="rate" stroke="#10b981" strokeWidth={2.5} dot={{ r: 2 }} />
                      {dailyRate.month === currentMonthKey && (() => {
                        const todayIso = new Date();
                        const m = String(todayIso.getMonth() + 1).padStart(2, "0");
                        const d = String(todayIso.getDate()).padStart(2, "0");
                        const todayStr = `${todayIso.getFullYear()}-${m}-${d}`;
                        return dailyRate.series.some((s) => s.date === todayStr) ? (
                          <ReferenceLine yAxisId="left" x={todayStr} stroke="#ef4444" strokeWidth={1.5} strokeDasharray="4 4" />
                        ) : null;
                      })()}
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Andamento mensile combinato: appuntamenti vs feedback vs recensioni Google */}
            <MonthlyTrendCombinedCard />
          </div>
        )}

        {/* Feedback list */}
        <div className="border-t border-slate-100 pt-6">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <MessageSquare className="w-4 h-4" />
            <span><strong className="text-slate-700">{total}</strong> feedback {monthFilter !== "all" ? "nel mese" : "ricevuti"}</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Mese</label>
              <Select value={monthFilter} onValueChange={setMonthFilter}>
                <SelectTrigger className="h-9 w-[200px] rounded-xl border-slate-200 capitalize" data-testid="month-filter">
                  <SelectValue placeholder="Tutti i mesi" />
                </SelectTrigger>
                <SelectContent className="select-content">
                  <SelectItem value="all">Tutti i mesi</SelectItem>
                  {availableMonths.map((m) => (
                    <SelectItem key={m.value} value={m.value} className="capitalize">{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {totalPages > 1 && (
              <div className="flex items-center gap-2" data-testid="feedback-pagination">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => handlePage(page - 1)}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm text-slate-500">{page} / {totalPages}</span>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => handlePage(page + 1)}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        </div>

        {loading ? (
          <div className="text-center py-16 text-slate-400">Caricamento...</div>
        ) : feedbacks.length === 0 ? (
          <div className="text-center py-16">
            <MessageSquare className="w-12 h-12 text-slate-200 mx-auto mb-3" />
            <p className="text-slate-400">Nessun feedback ricevuto ancora</p>
          </div>
        ) : (
          <div className="space-y-8">
            {groupedByMonth.map((group) => (
              <section key={group.key} data-testid={`feedback-group-${group.key}`}>
                <h3 className="text-base font-semibold text-slate-700 capitalize mb-3 flex items-center gap-3">
                  <span>{group.label}</span>
                  <span className="text-xs font-normal text-slate-400">
                    {group.items.length} {group.items.length === 1 ? "feedback" : "feedback"}
                  </span>
                  <span className="flex-1 border-t border-slate-200" />
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {group.items.map((fb, i) => {
                    const typeLabel = getTypeLabel(fb.patient_type);
                    const hasNote = !!(fb.internal_note && fb.internal_note.trim());
                    return (
                      <div key={fb.feedback_id || `${group.key}-${i}`} className="rounded-xl border border-slate-200 p-4 hover:border-slate-300 transition-colors flex flex-col relative group/card" data-testid={`feedback-item-${i}`}>
                        <div className="absolute top-3 right-3 z-30 flex items-center gap-2">
                          <button
                            onClick={() => openNote(fb)}
                            className={`transition-colors ${hasNote ? "text-amber-500 hover:text-amber-600" : "text-slate-300 hover:text-amber-500"}`}
                            data-testid={`note-feedback-${i}`}
                            title={hasNote ? "Modifica nota interna" : "Aggiungi nota interna"}
                          >
                            <StickyNote className="w-3.5 h-3.5" fill={hasNote ? "currentColor" : "none"} />
                          </button>
                          {isAdmin && (
                            <button
                              onClick={() => handleDelete(fb.feedback_id)}
                              className="text-slate-300 hover:text-red-500 transition-colors"
                              data-testid={`delete-feedback-${i}`}
                              title="Cancella feedback"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                        {fb.comment && fb.comment.length > 80 && (
                          <div className="hidden group-hover/card:flex absolute inset-0 bg-slate-800 text-white rounded-xl p-4 z-20 items-center">
                            <p className="text-xs italic whitespace-pre-wrap">"{fb.comment}"</p>
                          </div>
                        )}
                        <div className="mb-3 pr-12">
                          <div className="text-base font-bold text-slate-800">
                            {fb.patient_name || "Anonimo"}
                          </div>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            {typeLabel && (
                              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${getTypeBadgeClass(fb.patient_type)}`}>
                                {typeLabel}
                              </span>
                            )}
                            <span className="text-[11px] text-slate-400">
                              {new Date(fb.submitted_at).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" })}
                              {" "}
                              {new Date(fb.submitted_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}
                            </span>
                          </div>
                        </div>

                        <div className="space-y-1.5 flex-1">
                          {(fb.answers || []).map((a) => (
                            <div key={a.question_id} className="flex items-center justify-between">
                              <span className="text-xs text-slate-500 truncate mr-2">{qMap[a.question_id] || a.question_id}</span>
                              <DotRating rating={a.rating} />
                            </div>
                          ))}
                        </div>

                        {fb.comment && (
                          <div className="mt-3 pt-2 border-t border-slate-100">
                            <p className="text-xs text-slate-500 italic line-clamp-4">"{fb.comment}"</p>
                          </div>
                        )}

                        {hasNote && (
                          <div className="mt-3 pt-2 border-t border-amber-100 bg-amber-50/40 -mx-4 -mb-4 px-4 pb-3 rounded-b-xl">
                            <div className="text-[10px] font-semibold text-amber-700 uppercase tracking-wider mb-1">Nota interna</div>
                            <p className="text-xs text-slate-700 whitespace-pre-wrap line-clamp-3">{fb.internal_note}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        )}

        {/* Pagination + Internal note dialog ↓ */}
        </div>
      </div>

      <Dialog open={noteDialog.open} onOpenChange={(o) => !o && closeNote()}>
        <DialogContent className="max-w-md" data-testid="note-dialog">
          <DialogHeader>
            <DialogTitle>Nota interna</DialogTitle>
          </DialogHeader>
          <Textarea
            value={noteDialog.value}
            onChange={(e) => setNoteDialog((s) => ({ ...s, value: e.target.value }))}
            placeholder="Scrivi una nota su questo feedback (visibile solo allo staff)…"
            rows={6}
            className="resize-none"
            data-testid="note-textarea"
          />
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={closeNote} data-testid="note-cancel">Annulla</Button>
            <Button onClick={saveNote} disabled={savingNote} className="bg-[#3B42DD] hover:bg-[#2f36b8] text-white" data-testid="note-save">
              {savingNote ? "Salvataggio…" : "Salva nota"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Card: Andamento mensile combinato (Appuntamenti vs Feedback vs Recensioni Google)
//
// Mostra ultimi N mesi (6/12/18/24) come BarChart raggruppato a 3 serie.
// Si aspetta una risposta da `GET /api/feedback/monthly-trend-combined`
// con rows = [{ym, label, appointments, feedbacks, google_reviews}].
// ────────────────────────────────────────────────────────────────────
function MonthlyTrendCombinedCard() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [months, setMonths] = useState(12);
  const [hasReviews, setHasReviews] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data: resp } = await axios.get(
          `${API}/feedback/monthly-trend-combined`,
          { params: { months }, withCredentials: true },
        );
        if (cancelled) return;
        const rows = resp?.rows || [];
        setData(rows);
        setHasReviews(rows.some((r) => (r.google_reviews || 0) > 0));
      } catch (err) {
        console.debug("monthly-trend-combined:", err?.response?.status);
        if (!cancelled) setData([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [months]);

  return (
    <div
      className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5"
      data-testid="feedback-monthly-trend-combined"
    >
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h2 className="text-sm font-bold text-slate-700 flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-indigo-500" />
          Andamento mensile · Appuntamenti / Feedback / Recensioni Google
        </h2>
        <select
          value={months}
          onChange={(e) => {
            const v = e.target.value;
            setMonths(v === "all" ? "all" : Number(v));
          }}
          className="text-xs border border-slate-200 rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-indigo-300"
          data-testid="trend-combined-months-select"
        >
          <option value={6}>6 mesi</option>
          <option value={12}>12 mesi</option>
          <option value={18}>18 mesi</option>
          <option value={24}>24 mesi</option>
          <option value={36}>36 mesi</option>
          <option value="all">Tutti i mesi</option>
        </select>
      </div>
      {!hasReviews && !loading && (
        <div className="mb-3 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-1.5">
          Nessuna recensione Google ancora sincronizzata. Vai in <strong>Admin → Recensioni Google</strong> per
          collegare la scheda e avviare il primo sync.
        </div>
      )}
      {loading ? (
        <div className="h-72 flex items-center justify-center text-xs text-slate-400">
          Caricamento…
        </div>
      ) : data.length === 0 ? (
        <div className="h-72 flex items-center justify-center text-xs text-slate-400">
          Nessun dato disponibile
        </div>
      ) : (
        <div style={{ width: "100%", height: 320 }}>
          <ResponsiveContainer>
            <ComposedChart data={data} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
              <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} stroke="#cbd5e1" />
              <YAxis
                yAxisId="left"
                tick={{ fontSize: 11, fill: "#64748b" }}
                stroke="#cbd5e1"
                allowDecimals={false}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                tick={{ fontSize: 11, fill: "#f97316" }}
                stroke="#fed7aa"
                allowDecimals={false}
              />
              <Tooltip
                contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #e2e8f0" }}
                formatter={(value, name) => [value, name]}
              />
              <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
              <Bar
                yAxisId="left"
                dataKey="appointments"
                name="Appuntamenti"
                fill="#c7d2fe"
              />
              <Bar
                yAxisId="left"
                dataKey="feedbacks"
                name="Feedback"
                fill="#f59e0b"
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="google_reviews"
                name="Recensioni Google"
                stroke="#f97316"
                strokeWidth={2.5}
                dot={{ r: 3 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

